import { useLocation } from 'react-router-dom';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Box, Typography, Card, CardContent, useTheme, CardHeader, Container, Divider, useMediaQuery, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Helmet } from 'react-helmet';
import { RootState } from '../store/store';
import AppConstants from '../constants/AppConstants';
import GoogleSheetsService from '../services/GoogleSheetsService';

interface TemplateOption {
  id: number;
  tabName: string;
  googleSheetId?: number;
  menusCsv:string;
}

const EmbeddedDashboards: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [iframeSrc, setIframeSrc] = useState<string | undefined>(undefined); // blob/object URL fallback
  const [iframeHtml, setIframeHtml] = useState<string | undefined>(undefined); // prefer inline HTML via srcDoc
  const [selectedGoogleSheetId, setSelectedGoogleSheetId] = useState<number | undefined>(undefined);
  const previousBlobUrlRef = useRef<string | null>(null); // track previous blob URL for revocation

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = useLocation();
  const handleIframeLoad = () => setIsLoading(false);
  const handleIframeError = () => { setIsLoading(false); setHasError(true); };
  const queryParams = new URLSearchParams(location.search);
  const currentMenu = queryParams.get('menu');

  // Fetch all templates on mount
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await GoogleSheetsService.allGoogleSheetHtmlTemplates('', '');
        if (!isMounted) return;

        const list = (res || [])
          .filter((r: any) => {
            if (!r.menusCsv) return false;
            return r.menusCsv
              .split(',')
              .map((m: string) => m.trim().toLowerCase())
              .includes(currentMenu?.toLowerCase());
          })
          .map((r: any) => ({
            id: r.id,
            tabName: r.tabName || `Template ${r.id}`,
            googleSheetId: r.googleSheetId,
            menusCsv: r.menusCsv
          }));

        setTemplates(list);

        if (list.length > 0) {
          setSelectedTemplateId(list[0].id);
          setSelectedGoogleSheetId(list[0].googleSheetId);
        } else {
          setSelectedTemplateId('');
          setSelectedGoogleSheetId(undefined);
        }

        setIsLoading(false);
      } catch (e) {
        if (isMounted) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    })();
    return () => { isMounted = false; };
  }, [currentMenu]);
  // Simple in-memory cache so we don't refetch templates we've already downloaded
  const templateCacheRef = useRef<{ [id: number]: { html?: string; blobUrl?: string } }>({});

  const loadTemplate = useCallback(async (templateId: number | undefined, googleSheetId: number | undefined) => {
    if (!templateId || !googleSheetId) return;
    setIsLoading(true);
    setHasError(false);
    try {
      // Serve from cache if present
      const cached = templateCacheRef.current[templateId];
      if (cached) {
        if (cached.html) {
          setIframeHtml(cached.html);
          setIframeSrc(undefined);
        } else if (cached.blobUrl) {
          setIframeSrc(cached.blobUrl);
          setIframeHtml(undefined);
        }
        setIsLoading(false);
        return;
      }
      const fileResponse: any = await GoogleSheetsService.downloadGoogleSheetHtmlTemplate(templateId, googleSheetId);
      if (fileResponse?.data instanceof Blob) {
        if (previousBlobUrlRef.current) {
          URL.revokeObjectURL(previousBlobUrlRef.current);
        }
        // Attempt to read blob as text (HTML)
        const text = await fileResponse.data.text().catch(() => null);
        if (text) {
          setIframeHtml(text);
          setIframeSrc(undefined);
          templateCacheRef.current[templateId] = { html: text };
        } else {
          const blobUrl = URL.createObjectURL(fileResponse.data);
          previousBlobUrlRef.current = blobUrl;
          setIframeSrc(blobUrl);
          setIframeHtml(undefined);
          templateCacheRef.current[templateId] = { blobUrl };
        }
      } else {
        setHasError(true);
      }
    } catch (err) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // When the selected template changes or initial selection set
  useEffect(() => {
    if (selectedTemplateId) {
      loadTemplate(selectedTemplateId, selectedGoogleSheetId);
    }
  }, [selectedTemplateId, selectedGoogleSheetId, loadTemplate]);

  const handleTemplateChange = (event: any) => {
    const val = event.target.value;
    const template = templates.find((t) => t.id === val);
    setSelectedTemplateId(val);
    setSelectedGoogleSheetId(template?.googleSheetId);
  };

  // Cleanup blob URL on unmount
  useEffect(() => () => {
    if (previousBlobUrlRef.current) URL.revokeObjectURL(previousBlobUrlRef.current);
  }, []);

  return (
    <>
      <Helmet>
        <title>Sheets Dashboards | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <Card sx={{ height: 'calc(100vh - 120px)' }}>
            <CardHeader
              title={(
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isMobile ? 'center' : 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    width: '100%',
                    textAlign: isMobile ? 'center' : 'left'
                  }}
                >
                  <Typography variant="h3" sx={{ mr: 2, width: isMobile ? '100%' : 'auto' }}>
                    AI Dashboards
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id="template-select-label">Template</InputLabel>
                    <Select
                      labelId="template-select-label"
                      value={selectedTemplateId}
                      label="Template"
                      onChange={handleTemplateChange}
                      disabled={templates.length === 0}
                    >
                      {templates.map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.tabName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              sx={{
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                gap: isMobile ? '20px' : '0'
              }}
            />
            <Divider />
            <CardContent sx={{ padding: 0, height: 'calc(100% - 64px)', position: 'relative' }}>
              {isLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1
                  }}
                >
                  <Typography variant="body1">Loading dashboard...</Typography>
                </Box>
              )}

              <iframe
                src={iframeSrc}
                srcDoc={iframeHtml}
                title="Embedded Dashboard"
                width="100%"
                height="100%"
                frameBorder="0"
                style={{
                  border: 'none',
                  display: hasError ? 'none' : 'block'
                }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />

              {!iframeSrc && !iframeHtml && !hasError && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2">Select a template to view.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: any) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(EmbeddedDashboards);
